import {
  useGenerateOrderSlip,
  usePostOrder,
  usePostPrintBill,
} from '@api/pos/pos-transaction-api.rtk';
import useAppContext from '@hooks/useAppContext';
import useToast from '@hooks/useToast';
import { IPosOrder } from '@modules/point-of-sale/interface/orders/pos-orders.interface';
import {
  selectPOSPrinter,
  selectPOSPrinterMasterlist,
  selectPOSstationID,
  selectPOSTableDetails,
  selectPOSTableOrders,
  selectPOSTableTransactionID,
} from '@modules/point-of-sale/slice/pos-selector';
import { clearTableOrder } from '@modules/point-of-sale/slice/pos-table-area.slice';
import { Cancel, DeleteSweep, Print, ReceiptLong } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { Box, Button, Divider, Stack, Typography } from '@mui/material';
import { useAppDispatch, useAppSelector } from '@store/index';
import { selectPropertyID } from '@store/selector/property.selector';
import { formatNumber, parseNumber } from '@utils/format';
import { buildPosOrderPayload } from '@utils/pos-utils';
import { usePOSScreen } from '@utils/screen-size';
import { hashSignature } from '@utils/security';
import { useMemo, useState } from 'react';
import { getColor } from '../../../../../assets/theme';
import { usePOSOrderDetailsCtx } from './pos-order-taking-window';
import { ConfirmationDialog } from '@hooks/useConfirmationDialog';
/**
 * Props for the POSDiscountSection component.
 *
 * @typedef {Object} DiscountProps
 * @property {number} discount - The discount amount to be applied.
 * @property {number} netPayable - The net payable amount after discounts.
 * @property {function} openRemoveWarning - Function to open the remove warning dialog.
 * @property {function} openVoidWarning - Function to open the void warning dialog.
 * @property {IPosOrder[], React.Dispatch<React.SetStateAction<IPosOrder[]>>} editItemsState - State for managing edited items.
 */
interface DiscountProps {
  discount: number;
  netPayable: number;
  openRemoveWarning: () => void;
  openVoidWarning: () => void;
  editItemsState: [
    IPosOrder[],
    React.Dispatch<React.SetStateAction<IPosOrder[]>>,
  ];
}

/**
 * POSDiscountSection component displays the discount information and action buttons for the order.
 * It allows users to clear items, void items, and print the order summary.
 *
 * @param {DiscountProps} props - The props for the component.
 * @returns {JSX.Element} The rendered component.
 */
const POSDiscountSection = ({
  openRemoveWarning,
  openVoidWarning,
  editItemsState,
}: DiscountProps) => {
  const {
    //openSecCodeDialog,
    // startLoadingStatus,
    stopLoadingStatus,
  } = useAppContext();
  const { toast } = useToast();
  const { isBillOut } = usePOSOrderDetailsCtx();
  const [editItems, setEditItems] = editItemsState;
  const dispatch = useAppDispatch();
  const orders = useAppSelector(selectPOSTableOrders);
  const { discountBG, discText, white, borderColor } = getColor();
  const tableDetails = useAppSelector(selectPOSTableDetails);
  const transactionID = useAppSelector(selectPOSTableTransactionID);
  const propertyID = useAppSelector(selectPropertyID);
  const stationID = useAppSelector(selectPOSstationID);
  const printerID = useAppSelector(selectPOSPrinter);
  const isPOS1024p = usePOSScreen();
  const [showDialog, setShowDialog] = useState(false);
  const [isPrintingOS, setIsPrintingOS] = useState(false);
  const [isPrintingBillState, setIsPrintingBillState] = useState(false);

  const posPrinters = useAppSelector(selectPOSPrinterMasterlist);
  const filteredPrinters =
    posPrinters?.filter((p) => p.masterlistStationReferenceID === stationID) ||
    [];
  const hasNoPrinters = filteredPrinters.length === 0;

  const {
    isAllPrintedOS,
    isHavePrintedOS,
    isHaveNewItems,
    vatScLt,
    totalCost,
    isAllSynced,
    hasNewItem,
    noOrders,
  } = useMemo(() => {
    // Calculate the total cost of items in the order
    const totalCost =
      orders
        ?.filter((item) => !item.orderID)
        ?.reduce((total, item) => {
          return total + parseNumber(item.grossAmount?.toString());
        }, 0) ?? 0;

    const totalDiscount = formatNumber(
      orders?.reduce((total, item) => {
        return total + parseNumber(item.discountTotal?.toString());
      }, 0) ?? 0,
      2,
      2,
    );

    const vatScLt = formatNumber(
      parseNumber(tableDetails?.vatScLt?.toString() ?? '0'),
      2,
      2,
    );

    // Check if all items in the order are printed
    const isAllPrintedOS =
      orders?.every((item) => Boolean(item.isPrintedOS)) ?? false;

    const isAllSynced = orders?.every((item) => item.orderID) ?? false;

    const isHavePrintedOS =
      orders?.some((item) => Boolean(item.isPrintedOS)) ?? false;

    const isHaveNewItems = orders?.some(
      (item) => !Boolean(item.isPrintedOS) && !item.orderID,
    );

    const hasNewItem = orders?.some(
      (item) => !item.orderID || !item.isPrintedOS,
    );

    const noOrders = orders.length <= 0;

    return {
      totalCost,
      isAllPrintedOS,
      isHavePrintedOS,
      isHaveNewItems,
      totalDiscount,
      vatScLt,
      isAllSynced,
      hasNewItem,
      noOrders,
    };
  }, [orders, tableDetails]);

  const isVoid = useMemo(() => {
    // Check if the selected item is voided
    return editItems?.some((item) => item.isPrintedOS || item.orderID) ?? false;
  }, [editItems]);

  const [postOrder, { isLoading: isPostingOrder }] = usePostOrder();
  const [generateOrderSlip, { isLoading: isGenerating }] =
    useGenerateOrderSlip();
  const [printBill, { isLoading: isPrintingBill }] = usePostPrintBill();
  const isLoading = isPostingOrder || isGenerating || isPrintingBill;

  const onPrintOS = async () => {
    try {
      if (!transactionID) return;
      setIsPrintingOS(true);
      // Get unprinted orders
      const unprintedOrders = orders?.filter((o) => !o.isPrintedOS) ?? [];
      if (!unprintedOrders.length) {
        toast({ message: 'No orders found to print', severity: 'warning' });
        return;
      }

      if (!isAllSynced) {
        // startLoadingStatus('Syncing Orders');

        // --- COMBINED 1: Post Order ---
        await postOrder({
          transactionID,
          payload: {
            properties: [
              {
                property: {
                  propertyID,
                  posOrders: buildPosOrderPayload({
                    orders: unprintedOrders,
                    propertyID,
                    stationID,
                    includeExistingOrderID: true,
                  }),
                },
              },
            ],
          },
        }).unwrap();
      }
      // startLoadingStatus('Generating Order Slip');

      await generateOrderSlip({
        transactionID: transactionID,
        payload: {
          properties: [
            {
              property: {
                propertyID,
                signature: hashSignature([propertyID].join('')),
              },
            },
          ],
        },
      }).unwrap();

      toast({
        message: 'Order slip printed successfully',
        severity: 'success',
      });
    } catch (error) {
      toast({ message: error, severity: 'error' });
    } finally {
      setEditItems([]);
      stopLoadingStatus();
      setIsPrintingOS(false);
    }
  };

  const onPrintBill = async () => {
    // await openSecCodeDialog(async (res) => {
    try {
      setShowDialog(false);
      setIsPrintingBillState(true);
      if (!transactionID) {
        toast({ severity: 'warning', message: 'No Transaction ID Found' });
        return;
      }

      if (noOrders) {
        toast({ severity: 'warning', message: 'No orders found to print' });
        return;
      }

      if (!printerID) {
        toast({ message: 'No printer found.' });
        return;
      }

      // if (!res?.data.isValid) return;

      if (isHaveNewItems) {
        // startLoadingStatus('Syncing Orders');

        await postOrder({
          transactionID,
          payload: {
            properties: [
              {
                property: {
                  propertyID,
                  posOrders: buildPosOrderPayload({
                    orders:
                      orders?.filter(
                        (item) => !item.isPrintedOS && !item.orderID,
                      ) ?? [],
                    propertyID,
                    stationID,
                  }),
                },
              },
            ],
          },
          noRefetch: true,
        }).unwrap();

        // startLoadingStatus('Generating Orders');

        await printBill({
          transactionID,
          payload: {
            properties: [
              {
                property: {
                  propertyID,
                  signature: hashSignature([propertyID].join('')),
                },
              },
            ],
          },
          printerRefID: printerID || '',
        }).unwrap();

        dispatch(
          clearTableOrder({
            clearNoID: orders
              ?.filter((item) => !item.isPrintedOS && !item.orderID)
              ?.map((item) => item.menuID),
          }),
        );

        toast({
          severity: 'success',
          message: 'Billing slip printed successfully',
        });

        return;
      }

      // startLoadingStatus('Generating Orders');

      await printBill({
        transactionID,
        payload: {
          properties: [
            {
              property: {
                propertyID,
                signature: hashSignature([propertyID].join('')),
              },
            },
          ],
        },
        printerRefID: printerID || '',
      }).unwrap();

      dispatch(
        clearTableOrder({
          clearNoID: orders
            ?.filter((item) => !item.isPrintedOS && !item.orderID)
            ?.map((item) => item.menuID),
        }),
      );

      toast({
        severity: 'success',
        message: 'Billing slip printed successfully',
      });
    } catch (error) {
      toast({ severity: 'warning', message: error });
    } finally {
      stopLoadingStatus();
      setIsPrintingBillState(false);
    }
    // }, 'allow_print_bill');
  };

  return (
    <>
      <Stack
        sx={{
          bgcolor: discountBG,
          position: 'relative',
          mx: 2.5,
          borderRadius: '8px',
        }}
      >
        {/* UI ONLY */}
        <Box
          sx={{
            width: '18px',
            height: '18px',
            bgcolor: white,
            borderRadius: '100%',
            position: 'absolute',
            top: '50%',
            left: -10,
            transform: 'translateY(-50%)',
          }}
        />
        <Box
          sx={{
            width: '18px',
            height: '18px',
            bgcolor: white,
            borderRadius: '100%',
            position: 'absolute',
            top: '50%',
            right: -10,
            transform: 'translateY(-50%)',
          }}
        />
        {/* UI ONLY */}
        <Stack sx={{ p: 2, pt: 1 }} gap={1}>
          <Box>
            <Stack gap={0.2} pb={0.5}>
              <Stack
                direction='row'
                justifyContent='space-between'
                alignItems='center'
              >
                <Typography sx={{ fontSize: '14px', color: discText }}>
                  VAT/SC/LT:
                </Typography>
                <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                  {vatScLt}
                </Typography>
              </Stack>

              {tableDetails?.totalDeduction &&
                parseFloat(tableDetails?.totalDeduction) > 0 && (
                  <Stack
                    direction='row'
                    justifyContent='space-between'
                    alignItems='center'
                  >
                    <Typography sx={{ fontSize: '14px', color: discText }}>
                      Total Deduction:
                    </Typography>
                    <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                      {formatNumber(tableDetails.totalDeduction ?? 0, 2, 2)}
                    </Typography>
                  </Stack>
                )}
            </Stack>

            <Divider
              sx={{
                borderStyle: 'dashed',
                borderWidth: '1px',
                color: borderColor,
              }}
            />
          </Box>
          <Stack direction='row' justifyContent='space-between'>
            <Typography variant='h6' fontWeight={600}>
              Net Payables:
            </Typography>
            <Typography variant='h5' fontWeight={600}>
              {formatNumber(
                `${parseNumber(tableDetails?.netAmount ?? '0') + totalCost}`,
                2,
                2,
              )}
            </Typography>
          </Stack>
          {!isBillOut && (
            <Stack
              direction='row'
              gap={1}
              sx={
                isPOS1024p
                  ? {
                      button: { span: { m: 0 }, mt: 0.5, p: 1 },
                    }
                  : {}
              }
            >
              {orders && orders?.length > 0 && editItems?.length <= 0 && (
                <Button
                  sx={{
                    flexGrow: 1,
                    whiteSpace: 'nowrap',
                    bgcolor: '#ff56301a',

                    color: 'error.main',
                    ':hover': {
                      bgcolor: '#ff56302e',
                    },
                  }}
                  variant='contained'
                  color='error'
                  size={isPOS1024p ? 'medium' : 'large'}
                  startIcon={isVoid ? <Cancel /> : <DeleteSweep />}
                  onClick={
                    isVoid || isAllPrintedOS
                      ? openVoidWarning
                      : openRemoveWarning
                  }
                  disabled={isLoading}
                >
                  {isPOS1024p
                    ? ''
                    : isVoid || isAllPrintedOS
                      ? 'Void All'
                      : isHavePrintedOS
                        ? `Clear New`
                        : 'Clear All'}
                </Button>
              )}
              <LoadingButton
                sx={{
                  flexGrow: 1,
                  whiteSpace: 'nowrap',
                  bgcolor: '#2b349314',
                  color: 'primary.main',
                  ':hover': {
                    bgcolor: '#bbbfdc6b',
                  },
                }}
                variant='contained'
                color='primary'
                size={isPOS1024p ? 'medium' : 'large'}
                startIcon={<Print />}
                onClick={onPrintOS}
                loading={isPrintingOS}
                disabled={hasNoPrinters}
              >
                {isPOS1024p ? '' : 'Print OS'}
              </LoadingButton>
              <LoadingButton
                sx={{
                  flexGrow: 1,
                  whiteSpace: 'nowrap',
                  bgcolor: '#2b349314',
                  color: 'primary.main',
                  ':hover': {
                    bgcolor: '#bbbfdc6b',
                  },
                }}
                variant='contained'
                color='primary'
                size={isPOS1024p ? 'medium' : 'large'}
                startIcon={<ReceiptLong />}
                onClick={hasNewItem ? () => setShowDialog(true) : onPrintBill}
                loading={isPrintingBillState}
                disabled={hasNoPrinters}
              >
                {isPOS1024p ? '' : 'Print Bill'}
              </LoadingButton>
            </Stack>
          )}
        </Stack>
      </Stack>

      <ConfirmationDialog
        dialogStatus='warning'
        iconColor='warning'
        open={showDialog}
        title={`Pending OS for Printing`}
        desc={`Are you sure you want to bill out this transaction?\nThe order slip will be printed automatically.`}
        onConfirm={onPrintBill}
        onClose={() => setShowDialog(false)}
      />
    </>
  );
};

export default POSDiscountSection;
